import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import api from "../../services/api";
import { useFeatures } from "../../hooks/useFeatures";

/**
 * Turning on "parents can pay through the app".
 *
 * The money goes straight to the club's own account — we take nothing and we
 * never hold it — so most of what this card does is explain that, and then
 * get out of the way. The rest is honest about the one thing a club will not
 * expect: Stripe has to verify the club before it can take a card, and that
 * is not instant.
 *
 * Until it is on, nothing changes: the club keeps collecting cash exactly as
 * it does today.
 */

type ConnectStatus = {
  accountId: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  requirements: string[];
  status: "onboarding_required" | "pending_verification" | "active" | "restricted";
  can_collect: boolean;
  blocked_message: string | null;
};

/** Stripe's requirement keys are not sentences. These are the common ones. */
const REQUIREMENT_LABELS: Record<string, string> = {
  "external_account": "Contul bancar (IBAN) unde se fac plățile",
  "company.tax_id": "CUI-ul clubului",
  "company.verification.document": "Un document de verificare a clubului",
  "company.address.line1": "Adresa sediului",
  "representative.verification.document": "Actul de identitate al reprezentantului",
  "individual.verification.document": "Actul de identitate al reprezentantului",
  "business_profile.url": "Site-ul sau pagina clubului",
  "business_profile.mcc": "Domeniul de activitate",
  "tos_acceptance.date": "Acceptarea termenilor Stripe",
};

const labelFor = (key: string) =>
  REQUIREMENT_LABELS[key] ||
  REQUIREMENT_LABELS[key.replace(/^representative\./, "individual.")] ||
  key;

export default function ClubPaymentsCard({ clubId }: { clubId: string | null | undefined }) {
  const { features } = useFeatures();
  const [status, setStatus] = useState<ConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<"onboard" | "dashboard" | null>(null);

  const load = useCallback(async () => {
    if (!clubId) return;
    setLoading(true);
    try {
      const { data } = await api.get<ConnectStatus>(`/club-payments/${clubId}/status`);
      setStatus(data);
    } catch {
      setStatus(null);
    }
    setLoading(false);
  }, [clubId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Coming back from Stripe. The URL carries `payments=return`, and the state
   * at that moment is usually still "pending" — the account.updated webhook
   * lands a beat later — so this re-reads rather than assuming success.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payments") === "return" || params.get("payments") === "refresh") {
      load();
    }
  }, [load]);

  const openStripe = async (kind: "onboard" | "dashboard") => {
    if (!clubId) return;
    setBusy(kind);
    try {
      const path = kind === "onboard" ? "onboarding-link" : "dashboard-link";
      const { data } = await api.post<{ url: string }>(`/club-payments/${clubId}/${path}`);
      // Same tab: Stripe sends the club back to us with a return URL, and a
      // popup that gets blocked looks like a broken button.
      window.location.href = data.url;
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu am putut deschide Stripe. Încearcă din nou.");
      setBusy(null);
    }
  };

  // Nothing about collecting online while the platform has not turned it on —
  // a club must not start a Stripe onboarding it cannot finish using.
  if (!features.paymentsOnline) return null;
  if (!clubId) return null;

  if (loading) {
    return (
      <div className="p-5 bg-white border shadow-sm border-slate-200 rounded-2xl">
        <p className="text-sm text-slate-400">Se încarcă starea încasărilor...</p>
      </div>
    );
  }

  const state = status?.status || "onboarding_required";
  const active = state === "active" || state === "restricted";

  return (
    <div className="p-5 bg-white border shadow-sm border-slate-200 rounded-2xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-800">Încasări prin aplicație</h3>
          <p className="mt-0.5 text-sm text-slate-500">
            Părinții achită cotizațiile direct din aplicație, cu cardul.
          </p>
        </div>
        <StatusPill state={state} />
      </div>

      {/* The part a club actually wants to know, said once, plainly. */}
      <div className="p-4 mt-4 text-sm leading-relaxed rounded-xl bg-slate-50 text-slate-600">
        <p>
          Banii intră <strong>direct în contul clubului</strong>, nu la noi — nici nu trecem prin ei.
          Comisionul de procesare îl reține Stripe din încasare, iar{" "}
          <strong>platforma nu ia nimic în plus</strong>: plătești doar abonamentul obișnuit.
        </p>
      </div>

      {state === "onboarding_required" && (
        <>
          <p className="mt-4 text-sm leading-relaxed text-slate-600">
            Pentru activare, Stripe cere datele clubului și IBAN-ul pe care vrei să primești banii.
            Denumirea legală, CUI-ul și sediul le trimitem noi din ce avem deja, deci nu le mai scrii.
            Verificarea o face Stripe și poate dura până la câteva zile lucrătoare.
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Până la finalizare nu se schimbă nimic: încasezi numerar exact ca acum.
          </p>
          <button
            type="button"
            onClick={() => openStripe("onboard")}
            disabled={busy !== null}
            className="w-full px-4 py-3 mt-4 text-sm font-extrabold text-white transition-colors bg-indigo-600 sm:w-auto rounded-xl hover:bg-indigo-700 disabled:opacity-60"
          >
            {busy === "onboard" ? "Se deschide Stripe..." : "Activează încasările"}
          </button>
        </>
      )}

      {state === "pending_verification" && (
        <>
          <p className="mt-4 text-sm leading-relaxed text-slate-600">
            Ai trimis datele. Stripe le verifică — nu e nimic de făcut din partea ta. Îți apare aici
            ca activ imediat ce aprobă, fără să mai intri pe nimic.
          </p>
          {status && status.requirements.length > 0 && <Missing requirements={status.requirements} />}
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              type="button"
              onClick={() => openStripe("onboard")}
              disabled={busy !== null}
              className="px-4 py-2.5 text-sm font-semibold bg-white border text-slate-700 border-slate-300 rounded-xl hover:bg-slate-50 disabled:opacity-60"
            >
              {busy === "onboard" ? "Se deschide..." : "Completează datele"}
            </button>
            <button
              type="button"
              onClick={load}
              className="px-4 py-2.5 text-sm font-semibold text-indigo-600 rounded-xl hover:bg-indigo-50"
            >
              Verifică din nou
            </button>
          </div>
        </>
      )}

      {active && (
        <>
          <p className="mt-4 text-sm leading-relaxed text-slate-600">
            Încasările sunt active. Părinții văd ce au de plată în aplicație și achită cu cardul;
            banii ajung în contul tău conform programului de plăți Stripe.
          </p>

          {!status?.payoutsEnabled && (
            <p className="p-3 mt-3 text-sm rounded-xl bg-amber-50 text-amber-800">
              Încasările merg, dar plățile către contul tău bancar sunt încă blocate. Completează ce
              lipsește mai jos, altfel banii se strâng în contul Stripe fără să ajungă la tine.
            </p>
          )}

          {state === "restricted" && status && status.requirements.length > 0 && (
            <>
              <p className="p-3 mt-3 text-sm rounded-xl bg-amber-50 text-amber-800">
                Stripe încă așteaptă câteva lucruri. Până le trimiți, încasările pot fi oprite.
              </p>
              <Missing requirements={status.requirements} />
            </>
          )}

          <div className="flex flex-wrap gap-3 mt-4">
            <button
              type="button"
              onClick={() => openStripe("dashboard")}
              disabled={busy !== null}
              className="px-4 py-2.5 text-sm font-bold text-white bg-slate-800 rounded-xl hover:bg-slate-900 disabled:opacity-60"
            >
              {busy === "dashboard" ? "Se deschide..." : "Deschide panoul Stripe"}
            </button>
            {state === "restricted" && (
              <button
                type="button"
                onClick={() => openStripe("onboard")}
                disabled={busy !== null}
                className="px-4 py-2.5 text-sm font-semibold bg-white border text-slate-700 border-slate-300 rounded-xl hover:bg-slate-50 disabled:opacity-60"
              >
                Completează datele
              </button>
            )}
          </div>
          <p className="mt-3 text-xs text-slate-400">
            IBAN-ul, plățile și extrasele se administrează în panoul Stripe al clubului. Noi nu
            păstrăm datele tale bancare.
          </p>
        </>
      )}

      {status && !status.can_collect && status.blocked_message && active && (
        <p className="mt-3 text-sm text-rose-600">{status.blocked_message}</p>
      )}
    </div>
  );
}

function StatusPill({ state }: { state: ConnectStatus["status"] }) {
  const map: Record<ConnectStatus["status"], { label: string; cls: string }> = {
    onboarding_required: { label: "Neactivat", cls: "bg-slate-100 text-slate-500" },
    pending_verification: { label: "În verificare", cls: "bg-blue-50 text-blue-700" },
    active: { label: "Activ", cls: "bg-emerald-50 text-emerald-700" },
    restricted: { label: "Activ · date lipsă", cls: "bg-amber-50 text-amber-700" },
  };
  const { label, cls } = map[state];
  return <span className={`px-2.5 py-1 text-xs font-semibold rounded-lg ${cls}`}>{label}</span>;
}

/** Stripe's requirement keys, in Romanian, as a list the club can act on. */
function Missing({ requirements }: { requirements: string[] }) {
  return (
    <ul className="mt-3 space-y-1.5">
      {requirements.slice(0, 8).map((r) => (
        <li key={r} className="flex items-start gap-2 text-sm text-slate-600">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
          {labelFor(r)}
        </li>
      ))}
    </ul>
  );
}
